/**
 * Admin → Layout editor. Lets admins:
 *  - Pick alignment / max-width / vertical padding per element per device
 *  - Choose a hero variant (split-right / split-left / stacked / background)
 *  - Pick which scopes the layout applies to
 *  - Preview live (a small mock storefront block reflects the choices)
 *
 * Saves to `settings.layout` via the existing PUT /api/admin/settings.
 */
import React, { useMemo, useState } from 'react';
import axios from 'axios';
import { Save, RotateCcw, Monitor, Smartphone, AlignLeft, AlignCenter, AlignRight, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import {
  LAYOUT_ELEMENTS,
  LAYOUT_DEVICES,
  LAYOUT_ALIGN_OPTIONS,
  LAYOUT_HERO_VARIANTS,
  LAYOUT_HERO_ASPECT_OPTIONS,
  LAYOUT_HERO_RIGHT_CONTENT_OPTIONS,
  LAYOUT_BOUNDS,
  LAYOUT_DEFAULTS,
  resolveLayoutConfig,
} from '../lib/layout';
import {
  TYPOGRAPHY_SIZE_BOUNDS,
  TYPOGRAPHY_WEIGHT_OPTIONS,
  resolveTypographyConfig,
} from '../lib/typography';
import { useSettings } from '../context/SettingsContext';
import Rial from './Rial';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SCOPE_OPTIONS = [
  { value: 'storefront', label: 'Storefront', hint: 'Customer-facing pages' },
  { value: 'driver',     label: 'Driver portal', hint: '/driver/*' },
  { value: 'admin',      label: 'Admin panel',  hint: 'This page & all admin routes' },
];

const AlignButton = ({ value, current, onClick, icon: Icon, label }) => (
  <button
    type="button"
    onClick={() => onClick(value)}
    data-testid={`layout-align-${value}`}
    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition border ${
      current === value
        ? 'bg-[#0078D4] text-white border-[#0078D4]'
        : 'bg-white text-slate-600 border-slate-200 hover:border-[#0078D4]'
    }`}
    title={label}
  >
    <Icon className="w-3.5 h-3.5" />
    {label}
  </button>
);

const SliderRow = ({ label, hint, value, onChange, bounds }) => {
  const { min, max, step, unit } = bounds;
  const isSet = typeof value === 'number';
  return (
    <div className="flex items-center gap-3 py-1.5">
      <div className="flex-shrink-0 w-32">
        <div className="text-xs font-semibold text-slate-700">{label}</div>
        {hint && <div className="text-[10px] text-slate-400">{hint}</div>}
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={isSet ? value : min}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 accent-[#0078D4]"
      />
      <div className="w-20 text-right">
        {isSet ? (
          <span className="text-xs font-mono text-slate-700">{value}{unit}</span>
        ) : (
          <span className="text-[10px] uppercase tracking-wider text-slate-400">Auto</span>
        )}
      </div>
      {isSet && (
        <button
          type="button"
          onClick={() => onChange(null)}
          className="text-[10px] text-slate-400 hover:text-red-500"
          title="Clear override"
        >
          ✕
        </button>
      )}
    </div>
  );
};

/** HeroDeviceCard — all-in-one hero editor for ONE device (desktop OR mobile).
 *  Bundles every knob that matters for the hero block: variant, right-column
 *  content, panel shape/size/inner padding, text alignment / max-width /
 *  outer padding, and typography H1 size+weight + body size. */
const HeroDeviceCard = ({
  device,
  draft,
  setHeroVariant,
  setHeroBlock,
  setHeroRightContent,
  setField,
  typoDraft,
  setTypoSize,
  setTypoWeight,
}) => {
  const e = draft.elements.hero;
  const Icon = device === 'mobile' ? Smartphone : Monitor;
  const sizeBounds = (k) => TYPOGRAPHY_SIZE_BOUNDS[k];

  const onTypoSlider = (field, v) => setTypoSize(field, device, v);

  return (
    <div className="rounded-xl border-2 border-[#0078D4]/20 bg-gradient-to-br from-[#DEECF9]/40 to-white p-4 sm:p-5">
      <div className="flex items-center gap-2 mb-3">
        <div className="w-8 h-8 rounded-lg bg-[#0078D4] text-white flex items-center justify-center">
          <Icon className="w-4 h-4" />
        </div>
        <div>
          <div className="text-sm font-bold text-slate-900">Hero · {device === 'mobile' ? 'Mobile' : 'Desktop'}</div>
          <div className="text-[11px] text-slate-500">One-stop control for the {device} hero block.</div>
        </div>
      </div>

      <details open className="mb-3 rounded-lg bg-white border border-slate-200">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-slate-700 uppercase tracking-wider list-none">
          Structure
        </summary>
        <div className="px-3 pb-3 space-y-3">
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1.5">Hero variant</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
              {LAYOUT_HERO_VARIANTS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setHeroVariant(device, v.value)}
                  data-testid={`hero-card-variant-${device}-${v.value}`}
                  className={`p-1.5 rounded-md border text-[11px] font-semibold transition ${
                    draft.hero_variant[device] === v.value
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={v.hint}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <div className="text-[11px] font-semibold text-slate-600 mb-1.5">Right column content</div>
            <div className="grid grid-cols-3 gap-1.5">
              {LAYOUT_HERO_RIGHT_CONTENT_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setHeroRightContent(device, c.value)}
                  data-testid={`hero-card-right-${device}-${c.value}`}
                  className={`p-1.5 rounded-md border text-[11px] font-semibold transition ${
                    draft.hero_right_content[device] === c.value
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={c.hint}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      </details>

      <details open className="mb-3 rounded-lg bg-white border border-slate-200">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-slate-700 uppercase tracking-wider list-none">
          Blue panel size & padding
        </summary>
        <div className="px-3 pb-3">
          <div className="mb-2">
            <div className="text-[11px] font-semibold text-slate-600 mb-1">Panel shape</div>
            <div className="flex flex-wrap gap-1.5">
              {LAYOUT_HERO_ASPECT_OPTIONS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setHeroBlock('aspect_ratio', device, a.value)}
                  data-testid={`hero-card-aspect-${device}-${a.value.replace('/','-')}`}
                  className={`px-2.5 py-1 rounded-md text-[11px] font-semibold border ${
                    draft.hero_block.aspect_ratio[device] === a.value
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={a.hint}
                >
                  {a.label}
                </button>
              ))}
              {draft.hero_block.aspect_ratio[device] && (
                <button
                  type="button"
                  onClick={() => setHeroBlock('aspect_ratio', device, null)}
                  className="text-[10px] text-slate-400 hover:text-red-500 px-1.5"
                >
                  ✕
                </button>
              )}
            </div>
          </div>
          <SliderRow label="Fixed height"    hint="Overrides aspect"   value={draft.hero_block.height[device]}        onChange={(v) => setHeroBlock('height', device, v)}        bounds={LAYOUT_BOUNDS.hero_height} />
          <SliderRow label="Panel max-width" hint="Caps total width"   value={draft.hero_block.max_width[device]}     onChange={(v) => setHeroBlock('max_width', device, v)}     bounds={LAYOUT_BOUNDS.hero_max_width} />
          <SliderRow label="Inner padding"   hint="Inside blue panel"  value={draft.hero_block.inner_padding[device]} onChange={(v) => setHeroBlock('inner_padding', device, v)} bounds={LAYOUT_BOUNDS.hero_inner_padding} />
        </div>
      </details>

      <details open className="mb-3 rounded-lg bg-white border border-slate-200">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-slate-700 uppercase tracking-wider list-none">
          Text layout
        </summary>
        <div className="px-3 pb-3">
          <div className="flex items-center gap-2 mb-2 flex-wrap">
            <div className="text-[11px] font-semibold text-slate-600 w-24">Alignment</div>
            <div className="flex gap-1.5">
              <AlignButton value="left"   current={e.alignment[device]} onClick={(v) => setField('hero', 'alignment', device, v)} icon={AlignLeft}   label="Left" />
              <AlignButton value="center" current={e.alignment[device]} onClick={(v) => setField('hero', 'alignment', device, v)} icon={AlignCenter} label="Center" />
              <AlignButton value="right"  current={e.alignment[device]} onClick={(v) => setField('hero', 'alignment', device, v)} icon={AlignRight}  label="Right" />
              {e.alignment[device] && (
                <button type="button" onClick={() => setField('hero', 'alignment', device, null)} className="text-[10px] text-slate-400 hover:text-red-500 px-1.5">✕</button>
              )}
            </div>
          </div>
          <SliderRow label="Text max-width"  value={e.max_width[device]}      onChange={(v) => setField('hero', 'max_width', device, v)}      bounds={LAYOUT_BOUNDS.max_width} />
          <SliderRow label="Padding top"     value={e.padding_top[device]}    onChange={(v) => setField('hero', 'padding_top', device, v)}    bounds={LAYOUT_BOUNDS.padding_top} />
          <SliderRow label="Padding bottom"  value={e.padding_bottom[device]} onChange={(v) => setField('hero', 'padding_bottom', device, v)} bounds={LAYOUT_BOUNDS.padding_bottom} />
        </div>
      </details>

      <details open className="rounded-lg bg-white border border-slate-200">
        <summary className="cursor-pointer px-3 py-2 text-xs font-bold text-slate-700 uppercase tracking-wider list-none">
          Typography
        </summary>
        <div className="px-3 pb-3">
          <SliderRow label="H1 size"   hint="Hero headline"        value={typoDraft.sizes[device]?.h1}   onChange={(v) => onTypoSlider('h1', v)}   bounds={{...sizeBounds('h1'), unit: 'rem'}} />
          <SliderRow label="Body size" hint="Subtitle / paragraph" value={typoDraft.sizes[device]?.body} onChange={(v) => onTypoSlider('body', v)} bounds={{...sizeBounds('body'), unit: 'rem'}} />
          <div className="flex items-center gap-2 py-1.5">
            <div className="w-32 text-xs font-semibold text-slate-700">H1 weight</div>
            <select
              className="flex-1 text-xs border border-slate-200 rounded-md px-2 py-1.5"
              value={typoDraft.weights[device]?.h1 ?? ''}
              onChange={(ev) => setTypoWeight('h1', device, ev.target.value || null)}
              data-testid={`hero-card-h1-weight-${device}`}
            >
              {TYPOGRAPHY_WEIGHT_OPTIONS.map((opt) => (
                <option key={String(opt.value)} value={opt.value ?? ''}>{opt.label}</option>
              ))}
            </select>
          </div>
        </div>
      </details>
    </div>
  );
};


const ElementEditor = ({ elKey, label, hint, supportsHero, cfg, setField, heroVariant, setHeroVariant, heroBlock, setHeroBlock, heroRightContent, setHeroRightContent }) => {
  const [device, setDevice] = useState('desktop');
  const e = cfg.elements[elKey];

  const updateField = (field, value) => setField(elKey, field, device, value);

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <div className="text-sm font-bold text-slate-900">{label}</div>
          <div className="text-xs text-slate-500">{hint}</div>
        </div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {LAYOUT_DEVICES.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDevice(d)}
              data-testid={`layout-device-${elKey}-${d}`}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-semibold ${
                device === d ? 'bg-white shadow text-[#0078D4]' : 'text-slate-500'
              }`}
            >
              {d === 'desktop' ? <Monitor className="w-3.5 h-3.5" /> : <Smartphone className="w-3.5 h-3.5" />}
              {d === 'desktop' ? 'Desktop' : 'Mobile'}
            </button>
          ))}
        </div>
      </div>

      {supportsHero && (
        <div className="mb-3 pb-3 border-b border-slate-100 space-y-3">
          <div>
            <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase mb-2">Hero variant ({device})</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {LAYOUT_HERO_VARIANTS.map((v) => (
                <button
                  key={v.value}
                  type="button"
                  onClick={() => setHeroVariant(device, v.value)}
                  data-testid={`layout-hero-variant-${device}-${v.value}`}
                  className={`text-left p-2 rounded-lg border text-xs transition ${
                    heroVariant[device] === v.value
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={v.hint}
                >
                  <div className="font-semibold">{v.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase mb-2">Right column content ({device})</div>
            <div className="grid grid-cols-3 gap-2">
              {LAYOUT_HERO_RIGHT_CONTENT_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setHeroRightContent(device, c.value)}
                  data-testid={`layout-hero-right-${device}-${c.value}`}
                  className={`text-left p-2 rounded-lg border text-xs transition ${
                    heroRightContent[device] === c.value
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={c.hint}
                >
                  <div className="font-semibold">{c.label}</div>
                </button>
              ))}
            </div>
          </div>

          <div>
            <div className="text-[11px] font-bold tracking-wider text-slate-500 uppercase mb-2">Hero panel shape ({device})</div>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {LAYOUT_HERO_ASPECT_OPTIONS.map((a) => (
                <button
                  key={a.value}
                  type="button"
                  onClick={() => setHeroBlock('aspect_ratio', device, a.value)}
                  data-testid={`layout-hero-aspect-${device}-${a.value.replace('/','-')}`}
                  className={`px-3 py-1.5 rounded-lg text-xs font-semibold border transition ${
                    heroBlock.aspect_ratio[device] === a.value
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={a.hint}
                >
                  {a.label}
                </button>
              ))}
              {heroBlock.aspect_ratio[device] && (
                <button
                  type="button"
                  onClick={() => setHeroBlock('aspect_ratio', device, null)}
                  className="text-[10px] text-slate-400 hover:text-red-500 px-1.5"
                  title="Clear override"
                >
                  ✕ Clear
                </button>
              )}
            </div>
            <SliderRow label="Fixed height"  hint="Overrides aspect" value={heroBlock.height[device]}    onChange={(v) => setHeroBlock('height', device, v)}    bounds={LAYOUT_BOUNDS.hero_height} />
            <SliderRow label="Panel max-width" hint="Caps total width" value={heroBlock.max_width[device]} onChange={(v) => setHeroBlock('max_width', device, v)} bounds={LAYOUT_BOUNDS.hero_max_width} />
          </div>
        </div>
      )}

      <div className="flex items-center gap-2 mb-3">
        <div className="text-xs font-semibold text-slate-700 w-32">Alignment</div>
        <div className="flex flex-wrap gap-1.5">
          <AlignButton value="left" current={e.alignment[device]} onClick={(v) => updateField('alignment', v)} icon={AlignLeft} label="Left" />
          <AlignButton value="center" current={e.alignment[device]} onClick={(v) => updateField('alignment', v)} icon={AlignCenter} label="Center" />
          <AlignButton value="right" current={e.alignment[device]} onClick={(v) => updateField('alignment', v)} icon={AlignRight} label="Right" />
          {e.alignment[device] && (
            <button
              type="button"
              onClick={() => updateField('alignment', null)}
              className="text-[10px] text-slate-400 hover:text-red-500 px-1.5"
              title="Clear override"
            >
              ✕ Clear
            </button>
          )}
        </div>
      </div>

      <SliderRow label="Max width"      hint="Block's outer cap" value={e.max_width[device]}      onChange={(v) => updateField('max_width', v)}      bounds={LAYOUT_BOUNDS.max_width} />
      <SliderRow label="Padding top"    hint="Space above"        value={e.padding_top[device]}    onChange={(v) => updateField('padding_top', v)}    bounds={LAYOUT_BOUNDS.padding_top} />
      <SliderRow label="Padding bottom" hint="Space below"        value={e.padding_bottom[device]} onChange={(v) => updateField('padding_bottom', v)} bounds={LAYOUT_BOUNDS.padding_bottom} />
    </div>
  );
};

/** Inline-styled preview that mirrors the current draft without polluting
 * the real <html> CSS variables. Uses the same Tailwind classes so the
 * look matches the storefront. */
const LivePreview = ({ cfg }) => {
  const [device, setDevice] = useState('desktop');
  const variant = cfg.hero_variant[device] || 'split-right';

  const styleFor = (key) => {
    const e = cfg.elements[key];
    const align = e.alignment[device] ?? e.alignment.desktop;
    const mw    = e.max_width[device] ?? e.max_width.desktop;
    const pt    = e.padding_top[device] ?? e.padding_top.desktop;
    const pb    = e.padding_bottom[device] ?? e.padding_bottom.desktop;
    return {
      textAlign: align || undefined,
      maxWidth: typeof mw === 'number' ? `${mw}rem` : undefined,
      paddingTop: typeof pt === 'number' ? `${pt}rem` : undefined,
      paddingBottom: typeof pb === 'number' ? `${pb}rem` : undefined,
      marginLeft: align === 'left' ? 0 : align === 'right' ? 'auto' : 'auto',
      marginRight: align === 'right' ? 0 : align === 'left' ? 'auto' : 'auto',
    };
  };

  const heroGridStyle = (() => {
    if (variant === 'stacked' || device === 'mobile') return { gridTemplateColumns: '1fr' };
    if (variant === 'background') return { gridTemplateColumns: '1fr' };
    return { gridTemplateColumns: '7fr 5fr' };
  })();

  const heroOrderText = variant === 'split-left' ? 2 : 1;
  const heroOrderImage = variant === 'split-left' ? 1 : 2;

  // Hero block shape — translate "1/1" → "1 / 1" for CSS aspect-ratio.
  const heroAspect = (() => {
    const a = cfg.hero_block.aspect_ratio[device] ?? cfg.hero_block.aspect_ratio.desktop;
    if (!a) return undefined;
    if (a === 'auto') return 'auto';
    return a.replace('/', ' / ');
  })();
  const heroFixedHeight = cfg.hero_block.height[device] ?? cfg.hero_block.height.desktop;
  const heroPanelMaxW = cfg.hero_block.max_width[device] ?? cfg.hero_block.max_width.desktop;
  const heroPanelStyle = {
    aspectRatio: heroAspect,
    height: typeof heroFixedHeight === 'number' ? `${heroFixedHeight}rem` : undefined,
    maxWidth: typeof heroPanelMaxW === 'number' ? `${heroPanelMaxW}rem` : undefined,
    marginLeft: 'auto',
    marginRight: 'auto',
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white sticky top-4 self-start" data-testid="layout-preview">
      <div className="flex items-center justify-between px-4 pt-4 pb-2 border-b border-slate-100">
        <div className="text-sm font-bold text-slate-900">Live preview</div>
        <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-0.5">
          {LAYOUT_DEVICES.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => setDevice(d)}
              data-testid={`layout-preview-device-${d}`}
              className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-semibold ${
                device === d ? 'bg-white shadow text-[#0078D4]' : 'text-slate-500'
              }`}
            >
              {d === 'desktop' ? <Monitor className="w-3 h-3" /> : <Smartphone className="w-3 h-3" />}
              {d === 'desktop' ? 'Desktop' : 'Mobile'}
            </button>
          ))}
        </div>
      </div>

      <div className={`p-4 ${device === 'mobile' ? 'max-w-[380px] mx-auto' : ''}`}>
        {/* Hero preview */}
        {variant === 'background' ? (
          <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-[#0078D4] to-[#2563EB] text-white p-5 flex flex-col justify-center" style={{ ...heroPanelStyle, aspectRatio: heroPanelStyle.aspectRatio || '16 / 8' }}>
            <div className="absolute inset-0 opacity-30 bg-[radial-gradient(circle_at_30%_30%,#fff,transparent_60%)]" />
            <div style={styleFor('hero')} className="relative">
              <div className="text-[10px] uppercase tracking-widest text-cyan-200 mb-1">B2B trade portal</div>
              <div className="font-display text-2xl leading-tight">Bulk supplies, <span className="text-cyan-200">business ready.</span></div>
              <div className="text-xs text-blue-100 mt-1.5">Trade pricing & scheduled deliveries.</div>
            </div>
          </div>
        ) : (
          <div className="grid gap-3" style={heroGridStyle}>
            <div className="rounded-2xl bg-gradient-to-br from-[#0078D4] to-[#2563EB] p-4 text-white flex flex-col justify-center" style={{ ...heroPanelStyle, order: heroOrderText }}>
              <div style={styleFor('hero')}>
                <div className="text-[10px] uppercase tracking-widest text-cyan-200 mb-1">B2B trade portal</div>
                <div className="font-display text-lg sm:text-xl leading-tight">Bulk supplies, <span className="text-cyan-200">business ready.</span></div>
                <div className="text-[11px] text-blue-100 mt-1.5">Trade pricing & scheduled deliveries.</div>
              </div>
            </div>
            {(() => {
              const rc = cfg.hero_right_content[device];
              if (rc === 'empty') return null;
              if (rc === 'bestsellers') {
                return (
                  <div className="rounded-2xl bg-white border border-slate-200 p-2 text-xs flex flex-col" style={{ order: heroOrderImage, minHeight: 120 }}>
                    <div className="text-[9px] font-bold tracking-[0.2em] text-[#0078D4] uppercase mb-1.5 px-1">Bestsellers</div>
                    <div className="space-y-1.5 overflow-hidden">
                      {[1,2,3].map(i => (
                        <div key={i} className="flex items-center gap-2 p-1 bg-slate-50 rounded">
                          <div className="w-6 h-6 rounded bg-slate-200" />
                          <div className="text-[10px] text-slate-600 flex-1 truncate">Product {i}</div>
                          <div className="text-[10px] font-bold text-[#0078D4]"><Rial /> 0.500</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <div className="rounded-2xl bg-slate-100 flex items-center justify-center p-4 text-slate-400 text-xs" style={{ order: heroOrderImage, minHeight: 90 }}>
                  Featured image
                </div>
              );
            })()}
          </div>
        )}

        {/* Page title + subtitle preview */}
        <div className="mt-5">
          <h1 className="font-display text-2xl text-slate-900" style={styleFor('page_title')}>
            Page title
          </h1>
          <p className="text-xs text-slate-500 mt-1" style={styleFor('page_subtitle')}>
            A short sub-title that explains the page below the H1.
          </p>
        </div>

        {/* Section title preview */}
        <div className="mt-5">
          <h2 className="font-display text-lg text-slate-900" style={styleFor('section_title')}>
            Section title
          </h2>
          <div className="grid grid-cols-3 gap-2 mt-2">
            {[1,2,3].map(i => (
              <div key={i} className="aspect-square rounded-lg bg-slate-100" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const LayoutPanel = ({ authHeaders }) => {
  const { settings, refresh } = useSettings();
  const initial = useMemo(() => resolveLayoutConfig(settings?.layout), [settings]);
  const initialTypo = useMemo(() => resolveTypographyConfig(settings?.typography), [settings]);
  const [draft, setDraft] = useState(initial);
  const [typoDraft, setTypoDraft] = useState(initialTypo);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  // Sync if settings reload externally
  React.useEffect(() => {
    setDraft(resolveLayoutConfig(settings?.layout));
    setTypoDraft(resolveTypographyConfig(settings?.typography));
  }, [settings]);

  const dirty =
    JSON.stringify(draft) !== JSON.stringify(initial) ||
    JSON.stringify(typoDraft) !== JSON.stringify(initialTypo);

  const setField = (elKey, field, device, value) => {
    setDraft((d) => ({
      ...d,
      elements: {
        ...d.elements,
        [elKey]: {
          ...d.elements[elKey],
          [field]: { ...d.elements[elKey][field], [device]: value },
        },
      },
    }));
  };

  const setHeroVariant = (device, value) => {
    setDraft((d) => ({ ...d, hero_variant: { ...d.hero_variant, [device]: value } }));
  };

  const setHeroBlock = (field, device, value) => {
    setDraft((d) => ({
      ...d,
      hero_block: {
        ...d.hero_block,
        [field]: { ...d.hero_block[field], [device]: value },
      },
    }));
  };

  const setHeroRightContent = (device, value) => {
    setDraft((d) => ({ ...d, hero_right_content: { ...d.hero_right_content, [device]: value } }));
  };

  // Typography helpers — write to the local typo draft. Saved together
  // with the layout draft on Save.
  const setTypoSize = (field, device, value) => {
    setTypoDraft((t) => ({
      ...t,
      sizes: { ...t.sizes, [device]: { ...t.sizes[device], [field]: value } },
    }));
  };
  const setTypoWeight = (field, device, value) => {
    setTypoDraft((t) => ({
      ...t,
      weights: { ...t.weights, [device]: { ...t.weights[device], [field]: value } },
    }));
  };

  const toggleScope = (s) => {
    setDraft((d) => {
      const set = new Set(d.apply_to);
      if (set.has(s)) set.delete(s);
      else set.add(s);
      return { ...d, apply_to: Array.from(set) };
    });
  };

  const reset = () => {
    setDraft(resolveLayoutConfig(LAYOUT_DEFAULTS));
    setTypoDraft(resolveTypographyConfig({}));
  };
  const revert = () => {
    setDraft(initial);
    setTypoDraft(initialTypo);
  };

  const save = async () => {
    setSaving(true);
    setMsg(null);
    try {
      // PUT the FULL settings object back, with layout + typography merged.
      const payload = { ...settings, layout: draft, typography: typoDraft };
      delete payload._id;
      await axios.put(`${API}/admin/settings`, payload, { headers: authHeaders });
      await refresh();
      setMsg({ kind: 'ok', text: 'Layout saved.' });
      setTimeout(() => setMsg(null), 3000);
    } catch (e) {
      const errText = e.response?.data?.detail || e.message;
      setMsg({ kind: 'err', text: errText });
    } finally {
      setSaving(false);
    }
  };

  if (!settings) return <div className="p-6 text-sm text-slate-500">Loading layout settings…</div>;

  return (
    <div className="grid lg:grid-cols-[1fr_400px] gap-6">
      <div className="space-y-4" data-testid="layout-editor">
        {/* Scope picker */}
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <div className="text-sm font-bold text-slate-900 mb-2">Apply layout to</div>
          <div className="flex flex-wrap gap-2">
            {SCOPE_OPTIONS.map((s) => {
              const active = draft.apply_to.includes(s.value);
              return (
                <button
                  key={s.value}
                  type="button"
                  onClick={() => toggleScope(s.value)}
                  data-testid={`layout-scope-${s.value}`}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                    active
                      ? 'bg-[#0078D4] text-white border-[#0078D4]'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-[#0078D4]'
                  }`}
                  title={s.hint}
                >
                  {s.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Hero · Device cards — all-in-one editor for the hero block,
            split per device so admins (especially mobile-first) can tune
            every knob in one place. */}
        <HeroDeviceCard
          device="mobile"
          draft={draft}
          setHeroVariant={setHeroVariant}
          setHeroBlock={setHeroBlock}
          setHeroRightContent={setHeroRightContent}
          setField={setField}
          typoDraft={typoDraft}
          setTypoSize={setTypoSize}
          setTypoWeight={setTypoWeight}
        />
        <HeroDeviceCard
          device="desktop"
          draft={draft}
          setHeroVariant={setHeroVariant}
          setHeroBlock={setHeroBlock}
          setHeroRightContent={setHeroRightContent}
          setField={setField}
          typoDraft={typoDraft}
          setTypoSize={setTypoSize}
          setTypoWeight={setTypoWeight}
        />

        <div className="text-[11px] uppercase tracking-wider text-slate-400 font-bold pt-2">
          Advanced — per-element overrides
        </div>

        {/* Element editors */}
        {LAYOUT_ELEMENTS.map((el) => (
          <ElementEditor
            key={el.key}
            elKey={el.key}
            label={el.label}
            hint={el.hint}
            supportsHero={el.supportsHero}
            cfg={draft}
            setField={setField}
            heroVariant={draft.hero_variant}
            setHeroVariant={setHeroVariant}
            heroBlock={draft.hero_block}
            setHeroBlock={setHeroBlock}
            heroRightContent={draft.hero_right_content}
            setHeroRightContent={setHeroRightContent}
          />
        ))}

        {/* Sticky action bar */}
        <div className="sticky bottom-2 z-10 mt-6 flex items-center justify-between gap-3 bg-white border border-slate-200 rounded-xl shadow-lg p-3">
          <div className="text-xs">
            {msg && (
              <span className={msg.kind === 'ok' ? 'text-emerald-600' : 'text-red-600'}>{msg.text}</span>
            )}
            {!msg && dirty && <span className="text-amber-600 font-semibold">Unsaved changes</span>}
            {!msg && !dirty && <span className="text-slate-400">Up to date</span>}
          </div>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={reset} data-testid="layout-reset" disabled={saving}>
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Reset all
            </Button>
            <Button variant="outline" size="sm" onClick={revert} data-testid="layout-revert" disabled={saving || !dirty}>
              Revert
            </Button>
            <Button size="sm" onClick={save} disabled={saving || !dirty} data-testid="layout-save" className="bg-[#0078D4] hover:bg-[#005A9E]">
              <Save className="w-3.5 h-3.5 mr-1" /> {saving ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      </div>

      <LivePreview cfg={draft} />
    </div>
  );
};

export default LayoutPanel;
